import { createFileRoute, Link } from "@tanstack/react-router";
import { Fragment, useMemo, useState } from "react";
import {
  Search,
  CheckCircle2,
  AlertCircle,
  Copy,
  RefreshCw,
  Info,
  PackageSearch,
  ScanBarcode,
  Globe2,
} from "lucide-react";
import { ToolLayout } from "@/components/layout/ToolLayout";
import { TableScroll } from "@/components/layout/ContentBlock";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { FaqSection } from "@/components/seo/FaqSection";
import { toast } from "sonner";
import { ToolSeoSections } from "@/components/seo/ToolSeoSections";
import { pageHead, softwareAppJsonLd, breadcrumbJsonLd, ldScript } from "@/lib/seo";
import { validateEan13, validateEan8, computeCheckDigit, ean13Prefix } from "@/lib/gtin";
import { usePrefillValue } from "@/lib/usePrefillValue";

const PATH = "/ean-lookup";
const TITLE = "EAN Lookup — Validate EAN-13 & EAN-8 Barcodes Online";
const DESC =
  "Free EAN lookup tool. Validate EAN-13 and EAN-8 barcodes, verify the check digit, decode the GS1 country prefix, and link out to product catalogs.";

const FAQ = [
  {
    q: "What Is an EAN Barcode?",
    a: "EAN (European Article Number, now formally GTIN) is the global retail barcode standard. EAN-13 is the 13-digit form used worldwide on packaged goods, and EAN-8 is the compact 8-digit form used on small items where a full EAN-13 won't fit.",
  },
  {
    q: "How is the EAN-13 check digit calculated?",
    a: "Take the first 12 digits, multiply digits at odd positions (counted from the right, excluding the check digit) by 3 and even positions by 1, sum them, then the check digit is the value that makes the total a multiple of 10. ReadBarcode computes it for you and shows the expected digit if it doesn't match.",
  },
  {
    q: "Does the GS1 Country Prefix Tell Me Where a Product Is Made?",
    a: "No. The first three digits identify the GS1 Member Organisation that issued the manufacturer prefix — not the country of manufacture. A company registered with GS1 Germany can ship product made anywhere.",
  },
  {
    q: "What's the Difference Between UPC, EAN and GTIN?",
    a: "UPC-A is the 12-digit US/Canada retail code; EAN-13 is the 13-digit global standard; GTIN is the umbrella identifier that includes GTIN-8, GTIN-12 (UPC-A), GTIN-13 (EAN-13) and GTIN-14 (logistics). A UPC-A is just an EAN-13 with a leading zero.",
  },
  {
    q: "Why Does My EAN Fail Validation?",
    a: "Almost always one of: a mistyped digit, missing/extra digit, letters or punctuation mixed in, or you pasted an EAN-13 into an EAN-8 field. The error message shows the expected check digit so you can find the typo.",
  },
  {
    q: "Can This Tool Look up the Actual Product Name?",
    a: "Not yet — ReadBarcode validates the structure and country prefix locally. Product lookup depends on public catalogs and many private-label items aren't listed. The validated code can be sent to external catalog searches with one click.",
  },
  {
    q: "What Does EAN Mean?",
    a: "EAN stands for European Article Number, the name of the numbering system before it became the global GS1 system. The numbers themselves are now called GTIN-13 and GTIN-8, while EAN-13 and EAN-8 remain the names of the barcode symbols that carry them.",
  },
  {
    q: "Is Barcode Lookup Free?",
    a: "Validating a code on this page is free and needs no sign-up. Product databases are run by third parties, and some limit free searches or charge for bulk or API access. Check their terms before you rely on them for a product feed.",
  },
  {
    q: "What Is an EAN Checker?",
    a: "An EAN checker recalculates the last digit of an EAN-13 or EAN-8 from the other digits and compares it with the printed one. A mismatch means the code was mistyped or printed wrong. This page runs that check and also shows the GS1 prefix, the body and, for codes starting with 0, the UPC-A equivalent.",
  },
];

const CRUMBS = [
  { name: "Home", path: "/" },
  { name: "Lookup & Validators", path: "/lookup-validators" },
  { name: "EAN Lookup", path: PATH },
];

export const Route = createFileRoute("/ean-lookup")({
  component: EanLookupPage,
  head: () => {
    const base = pageHead({ title: TITLE, description: DESC, path: PATH });
    return {
      ...base,
      scripts: [
        ldScript(
          softwareAppJsonLd({
            name: "ReadBarcode EAN Lookup",
            description: DESC,
            path: PATH,
          }),
        ),
        ldScript(breadcrumbJsonLd(CRUMBS)),
      ],
    };
  },
});

function EanLookupPage() {
  return (
    <ToolLayout
      eyebrow="Online Tool"
      title="EAN Lookup & Validator"
      description="Validate EAN-13 and EAN-8 codes, verify the check digit and decode the GS1 country prefix. Lookup results depend on available product data. A 13-digit code that starts with 0 is also shown as its 12-digit UPC-A, and a wrong check digit comes with the corrected full number to compare."
      crumbs={CRUMBS}
      content={
        <>
          <DataAvailabilityNote />
          <Ean13vsEan8 />
          <ToolSeoSections
            aboutHeading="About this EAN lookup tool"
            about={
              <>
                <p>
                  This EAN lookup tool validates EAN-13 and EAN-8 barcodes — the global retail
                  identifier you'll see on almost every packaged product outside North America (and on
                  many inside it). It verifies the GS1 mod-10 check digit, splits the code into its
                  country prefix, manufacturer and item parts, and shows the equivalent UPC-A when
                  applicable.
                </p>
                <p>
                  All validation runs locally in your browser. A real product database integration is on
                  the roadmap; for now, validated codes can be sent to external catalog searches with
                  one click.
                </p>
              </>
            }
            whenToUse={[
              "Verifying an EAN-13 before printing labels for a new product run.",
              "Catching a mistyped digit in a supplier's product feed.",
              "Confirming a code is EAN-8 (small package) versus EAN-13.",
              "Decoding the GS1 country prefix to spot grey-market or in-store ranges.",
              "Converting an EAN-13 starting with 0 to its UPC-A equivalent for US catalogs.",
            ]}
            howItWorks={[
              {
                title: "Paste the EAN",
                desc: "Type or paste the digits. Spaces and dashes are ignored automatically.",
              },
              {
                title: "Auto-detect length",
                desc: "We treat 13-digit input as EAN-13 and 8-digit input as EAN-8.",
              },
              {
                title: "Validate the check digit",
                desc: "GS1 mod-10 algorithm; mismatched codes show the expected check digit.",
              },
              {
                title: "Decode the structure",
                desc: "GS1 country prefix, manufacturer/item body, and check digit are shown separately.",
              },
              {
                title: "Look up externally",
                desc: "Send the validated code to public catalogs in a new tab if you need product info.",
              },
            ]}
            whatToAvoid={[
              "Trusting a code that fails the check-digit test — it's almost certainly wrong.",
              "Assuming the country prefix tells you where the product was manufactured.",
              "Mixing up EAN-13 (13 digits) and EAN-8 (8 digits) when copy-pasting from spreadsheets.",
              "Paying for third-party product lookup before validating the code is structurally correct.",
              "Expecting in-store codes (020–029, 040–049 and 200–299 prefixes) to map to anything outside a single retailer.",
            ]}
            tips={[
              "Strip spaces, dashes and any leading apostrophes Excel adds before validating.",
              "If your EAN-13 starts with 0, it is a UPC-A — both forms are equivalent.",
              "Codes starting with 977 are serial publications (ISSN); 978 and 979 are books (ISBN).",
              "For book lookups, use the ISBN reader instead — it handles ISBN-10 too.",
              "Save validated codes to a spreadsheet before sending them to a label printer.",
            ]}
          />
          <PrefixTable />
          <h2>Country of Origin From a Barcode: What the Prefix Means</h2>
          <p>A barcode does not show a product's country of origin. The first three digits of an EAN-13 name the GS1 Member Organization that licensed the company prefix, not the country where the goods were made.</p>
          <p>For any valid EAN-13, the result above shows that prefix and its member organization. The legal country of origin is the one printed in the label text.</p>
          <h3>Japan Barcode Numbers (45 and 49)</h3>
          <p>An EAN-13 starting with 45 or 49 was issued through GS1 Japan, which manages the ranges 450–459 and 490–499. Japanese brands that produce overseas keep that prefix.</p>
          <h3>China Barcode Prefixes (690 to 699)</h3>
          <p>Codes from 690 to 699 were licensed through GS1 China. A 69 prefix shows that the brand owner holds a Chinese company prefix; it does not prove the item was made in China.</p>
          <h2>How to Find an EAN Number</h2>
          <p>The EAN number is the 13 digits, or 8 on small packs, printed under the barcode on the packaging. On an EAN-13, the first digit sits to the left of the bars.</p>
          <p>Type the digits above to confirm the EAN code is valid, or scan worn bars with the <Link to="/barcode-reader">online barcode reader</Link>.</p>
          <h3>EAN Number Lookup by Product</h3>
          <p>Shop and marketplace product pages often list the EAN or GTIN among the specifications. After validation, the Google and Barcode Lookup buttons on this page run an EAN search the other way, from number to product.</p>
          <p>US catalogs may want the 12-digit UPC-A form, covered in our <Link to="/barcode-guides/upc-vs-ean">guide to UPC vs EAN</Link>. For 14-digit carton codes, use the <Link to="/gtin-validator">GTIN validator</Link>.</p>
          <FaqSection items={FAQ} />
        </>
      }
      related={<RelatedTools />}
    >
      <EanLookupApp />
    </ToolLayout>
  );
}

type Result =
  | { kind: "idle" }
  | { kind: "error"; reason: string; expected?: number; hint?: string }
  | {
      kind: "ok";
      length: 8 | 13;
      normalized: string;
      checkDigit: number;
      prefix?: { prefix: string; description: string };
      upcA?: string;
    };

function EanLookupApp() {
  const [value, setValue] = useState("");
  const [submitted, setSubmitted] = useState(false);
  usePrefillValue((v) => {
    setValue(v);
    setSubmitted(true);
  });

  const result = useMemo<Result>(() => {
    if (!submitted) return { kind: "idle" };
    const raw = value.replace(/[\s-]+/g, "");
    if (!raw) return { kind: "error", reason: "Enter an EAN-13 (13 digits) or EAN-8 (8 digits)." };
    if (!/^\d+$/.test(raw)) {
      return {
        kind: "error",
        reason: "EAN codes contain digits only — remove any letters or punctuation.",
      };
    }

    if (raw.length === 13) {
      const v = validateEan13(raw);
      if (!v.ok) {
        const expected = computeCheckDigit(raw.slice(0, 12));
        return {
          kind: "error",
          reason: v.reason,
          expected,
          hint: `If the first 12 digits are correct, the full EAN-13 should be ${raw.slice(0, 12)}${expected}.`,
        };
      }
      const prefix = ean13Prefix(raw);
      const upcA = raw.startsWith("0") ? raw.slice(1) : undefined;
      return { kind: "ok", length: 13, normalized: raw, checkDigit: v.checkDigit, prefix, upcA };
    }

    if (raw.length === 8) {
      const v = validateEan8(raw);
      if (!v.ok) {
        const expected = computeCheckDigit(raw.slice(0, 7));
        return {
          kind: "error",
          reason: v.reason,
          expected,
          hint: `If the first 7 digits are correct, the full EAN-8 should be ${raw.slice(0, 7)}${expected}.`,
        };
      }
      return { kind: "ok", length: 8, normalized: raw, checkDigit: v.checkDigit };
    }

    return {
      kind: "error",
      reason: `EAN must be 13 digits (EAN-13) or 8 digits (EAN-8). You entered ${raw.length}.`,
    };
  }, [submitted, value]);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  const reset = () => {
    setValue("");
    setSubmitted(false);
  };

  return (
    <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
      <Card>
        <CardContent className="p-5 md:p-6">
          <form onSubmit={onSubmit} className="flex flex-col gap-3">
            <label htmlFor="ean-input" className="text-sm font-medium">
              EAN code
            </label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input
                id="ean-input"
                inputMode="numeric"
                autoComplete="off"
                placeholder="e.g. 5012345678900"
                maxLength={20}
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setSubmitted(false);
                }}
                aria-invalid={result.kind === "error"}
              />
              <Button type="submit" disabled={!value.trim()}>
                <Search className="mr-2 h-4 w-4" /> Validate
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">
              Accepts EAN-13 (13 digits) or EAN-8 (8 digits). Spaces and dashes are ignored.
            </p>
            <div className="flex flex-wrap gap-2 pt-1 text-xs">
              {EXAMPLES.map((ex) => (
                <button
                  key={ex}
                  type="button"
                  onClick={() => {
                    setValue(ex);
                    setSubmitted(true);
                  }}
                  className="rounded-md border border-border bg-background px-2 py-1 font-mono text-xs text-muted-foreground hover:border-primary/40 hover:text-foreground"
                >
                  {ex}
                </button>
              ))}
            </div>
            <div>
              <Button type="button" size="sm" variant="ghost" onClick={reset}>
                <RefreshCw className="mr-2 h-3.5 w-3.5" /> Clear
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <ResultPanel result={result} />
    </div>
  );
}

const EXAMPLES = ["5012345678900", "4006381333931", "73513537"];

function ResultPanel({ result }: { result: Result }) {
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard");
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };

  return (
    <Card>
      <CardContent className="flex h-full flex-col p-5 md:p-6">
        <p className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Result
        </p>
        <div className="mt-4 flex-1">
          {result.kind === "idle" && (
            <EmptyState>
              Enter an EAN and press Validate. We'll verify the check digit and show the structure
              breakdown.
            </EmptyState>
          )}

          {result.kind === "error" && (
            <div className="flex flex-col gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Invalid EAN</span>
              </div>
              <p className="text-sm text-muted-foreground">{result.reason}</p>
              {result.hint && (
                <p className="rounded-md border border-border bg-background p-3 text-sm">
                  <Info className="mr-2 inline h-3.5 w-3.5 text-primary" />
                  {result.hint}
                </p>
              )}
            </div>
          )}

          {result.kind === "ok" && (
            <div className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center gap-2 text-emerald-600 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-sm font-medium">Valid EAN-{result.length}</span>
              </div>
              <Row label={`EAN-${result.length}`} value={result.normalized} mono onCopy={copy} />
              {result.upcA && (
                <Row label="UPC-A equivalent" value={result.upcA} mono onCopy={copy} />
              )}
              {result.length === 13 ? (
                <div className="grid grid-cols-3 gap-2 rounded-lg border border-border bg-background p-3 text-center font-mono text-sm">
                  <Segment label="GS1 prefix" value={result.normalized.slice(0, 3)} />
                  <Segment label="Body" value={result.normalized.slice(3, 12)} />
                  <Segment label="Check" value={String(result.checkDigit)} />
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 rounded-lg border border-border bg-background p-3 text-center font-mono text-sm">
                  <Segment label="Body" value={result.normalized.slice(0, 7)} />
                  <Segment label="Check" value={String(result.checkDigit)} />
                </div>
              )}
              {result.prefix && (
                <div className="flex items-start gap-2 rounded-md border border-border bg-background p-3 text-sm">
                  <Globe2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden />
                  <div>
                    <div className="text-xs uppercase tracking-wide text-muted-foreground">
                      GS1 country prefix
                    </div>
                    <div className="mt-0.5">
                      <span className="font-mono">{result.prefix.prefix}</span> —{" "}
                      <span className="text-muted-foreground">{result.prefix.description}</span>
                    </div>
                  </div>
                </div>
              )}

              <ProductLookupArea ean={result.normalized} />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function ProductLookupArea({ ean }: { ean: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border bg-muted/30 p-4">
      <div className="flex items-center gap-2">
        <PackageSearch className="h-4 w-4 text-primary" aria-hidden />
        <p className="text-sm font-semibold">Product lookup</p>
        <Badge variant="outline" className="text-xs">
          Coming soon
        </Badge>
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        Product database integration is on the roadmap. Coverage of EAN codes in public catalogs
        varies a lot — many private-label or regional items aren't listed anywhere.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button asChild size="sm" variant="outline">
          <a
            href={`https://www.google.com/search?q=${encodeURIComponent("EAN " + ean)}`}
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            Search Google for this EAN
          </a>
        </Button>
        <Button asChild size="sm" variant="outline">
          <a
            href={`https://www.barcodelookup.com/${encodeURIComponent(ean)}`}
            target="_blank"
            rel="noopener noreferrer nofollow"
          >
            Try Barcode Lookup
          </a>
        </Button>
      </div>
      <p className="mt-2 text-xs text-muted-foreground">
        External links open in a new tab. ReadBarcode doesn't endorse third-party databases — verify
        before relying on any result.
      </p>
    </div>
  );
}

function Segment({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-base break-all">{value}</div>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
  onCopy,
}: {
  label: string;
  value: string;
  mono?: boolean;
  onCopy: (s: string) => void;
}) {
  return (
    <div className="flex items-start justify-between gap-3 rounded-md border border-border bg-background p-3">
      <div className="min-w-0">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
        <div className={`mt-0.5 break-all text-sm ${mono ? "font-mono" : ""}`}>{value}</div>
      </div>
      <Button size="sm" variant="ghost" onClick={() => onCopy(value)} aria-label={`Copy ${label}`}>
        <Copy className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}

function EmptyState({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex h-full min-h-32 flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border bg-muted/30 p-6 text-center">
      <ScanBarcode className="h-7 w-7 text-muted-foreground" aria-hidden />
      <p className="max-w-sm text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function DataAvailabilityNote() {
  return (
    <>
      <h2>About EAN lookup data</h2>
      <p>
        There is no single official global database of EAN codes. Public catalogs vary in coverage
        — especially for private-label, regional or older items. ReadBarcode's validator works <strong>100% offline in your browser</strong>,
        with no API key required. The product-name integration is on the roadmap.
      </p>
    </>
  );
}

function Ean13vsEan8() {
  const cards = [
    {
      title: "EAN-13",
      points: [
        "13 digits — the global retail standard outside North America.",
        "Structure: GS1 prefix + manufacturer + product + check digit.",
        "A US/Canada UPC-A is the same code with a leading zero (EAN-13 0xxxxxxxxxxxx).",
      ],
    },
    {
      title: "EAN-8",
      points: [
        "8 digits, assigned by GS1 for genuinely small packages.",
        "Not a compressed EAN-13 — it is a separate, independently issued number.",
        "Use only when an EAN-13 physically won't fit; capacity is limited.",
      ],
    },
  ];
  return (
    <>
      <h2>EAN-13 vs EAN-8</h2>
      <p>
        Both are valid retail barcodes, but they're issued and used differently. This validator
        checks the structure and check digit for each and decodes the GS1 country prefix where one
        applies.
      </p>
      {cards.map((c) => (
        <Fragment key={c.title}>
          <p>
            <strong>{c.title}</strong>
          </p>
          <ul>
            {c.points.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ul>
        </Fragment>
      ))}
    </>
  );
}

function PrefixTable() {
  const rows = [
    { range: "001–019, 030–039, 060–139", desc: "GS1 US — United States, and many Canadian companies (UPC-A as EAN-13 with leading 0)" },
    { range: "200–299", desc: "In-store / restricted distribution (not for retail)" },
    { range: "300–379", desc: "France & Monaco" },
    { range: "400–440", desc: "Germany" },
    { range: "450–459 / 490–499", desc: "Japan" },
    { range: "500–509", desc: "United Kingdom" },
    { range: "690–699", desc: "China" },
    { range: "754–755", desc: "Canada" },
    { range: "760–769", desc: "Switzerland & Liechtenstein" },
    { range: "800–839", desc: "Italy, San Marino & Vatican City" },
    { range: "840–849", desc: "Spain & Andorra" },
    { range: "870–879", desc: "Netherlands" },
    { range: "977", desc: "Serial publications (ISSN)" },
    { range: "978–979", desc: "Books (ISBN)" },
    { range: "981–984 / 990–999", desc: "GS1 coupon identification" },
  ];
  return (
    <>
      <h2>EAN-13 country prefix reference</h2>
      <p>
        The first three digits identify the GS1 Member Organisation that issued the manufacturer
        prefix. This is not the country of manufacture — only of registration. Full table: <a
          href="https://www.gs1.org/standards/id-keys/company-prefix"
          target="_blank"
          rel="noopener noreferrer nofollow"
        >
          GS1 prefix list
        </a>.
      </p>
      <TableScroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Prefix range</th>
              <th scope="col">Issuing GS1 Member</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.range}>
                <td>
                  <code>{r.range}</code>
                </td>
                <td>{r.desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </>
  );
}
function RelatedTools() {
  const related = [
    {
      path: "/upc-lookup",
      label: "UPC Lookup",
      desc: "Validate UPC-A and UPC-E codes used in the US and Canada.",
    },
    {
      path: "/gtin-validator",
      label: "GTIN Validator",
      desc: "Verify GTIN-8/12/13/14 check digits in one place.",
    },
    {
      path: "/check-digit-calculator",
      label: "Check Digit Calculator",
      desc: "Compute the correct UPC/EAN/GTIN check digit from the body.",
    },
    {
      path: "/barcode-reader",
      label: "Online Barcode Reader",
      desc: "Scan an EAN with your camera or upload an image.",
    },
    {
      path: "/read-barcode-from-image",
      label: "Read Barcode From Image",
      desc: "Decode an EAN from a photo or screenshot.",
    },
    {
      path: "/isbn-barcode-reader",
      label: "ISBN Barcode Reader",
      desc: "EAN-13s starting with 978/979 — validate them as ISBNs.",
    },
    {
      path: "/barcode-guides/upc-vs-ean",
      label: "UPC vs EAN Guide",
      desc: "When to use the 12-digit UPC-A or the 13-digit EAN-13.",
    },
  ];
  return (
    <section className="mt-12">
      <h2 className="text-2xl font-semibold tracking-tight">Related Tools</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        {related.map((r) => (
          <a
            key={r.path}
            href={r.path}
            className="group rounded-lg border border-border bg-background p-4 transition-colors hover:border-primary/40"
          >
            <div className="text-sm font-semibold group-hover:text-primary">{r.label}</div>
            <div className="mt-1 text-sm text-muted-foreground">{r.desc}</div>
          </a>
        ))}
      </div>
    </section>
  );
}
